import React, { useState, useEffect } from 'react';
import InvoicesPage from './Invoices';
import { collection, onSnapshot, query, orderBy, limit, addDoc, Timestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { Account, Invoice, Expense, CashRequisition } from '../types';
import { 
  DollarSign, 
  FileText, 
  TrendingUp, 
  Wallet, 
  ArrowUpRight, 
  ArrowDownRight, 
  Clock,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Plus,
  Trash2,
  X,
  Loader2,
  PieChart as PieChartIcon,
  TrendingDown,
  ChevronDown,
  Download,
  Printer,
  RefreshCw,
  Upload,
  FileSpreadsheet
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { cn } from '../lib/utils';
import { format, subMonths, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { toast } from 'sonner';
import AccountingReports, { AccountingReportsRef } from '../components/AccountingReports';
import { jsPDF } from 'jspdf';
import { downloadFile } from '../lib/downloadHelper';
import Papa from 'papaparse';
import ConfirmationModal from '../components/ConfirmationModal';

interface PDFPreviewData {
  url: string;
  doc: jsPDF;
  filename: string;
}

const AccountsDashboard: React.FC = () => {
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [requisitions, setRequisitions] = useState<CashRequisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'requisitions' | 'cashier'>('overview');
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState({
    description: '',
    amount: 0,
    category: 'General',
    date: new Date().toISOString().split('T')[0],
    accountId: ''
  });

  const expenseCategories = [
    'General',
    'Salaries',
    'Rent',
    'Utilities',
    'Equipment',
    'Maintenance',
    'Marketing',
    'Travel',
    'Taxes',
    'Administration',
    'Operations',
    'Other'
  ];

  useEffect(() => {
    const unsubscribeAccounts = onSnapshot(collection(db, 'accounts'), (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'accounts');
    });
    
    const qInvoices = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
    const unsubscribeInvoices = onSnapshot(qInvoices, (snapshot) => {
      setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invoice)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'invoices');
    });

    const qExpenses = query(collection(db, 'expenses'), orderBy('date', 'desc'));
    const unsubscribeExpenses = onSnapshot(qExpenses, (snapshot) => {
      setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expense)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'expenses');
    });

    const qReq = query(collection(db, 'cash_requisitions'), orderBy('createdAt', 'desc'));
    const unsubscribeReq = onSnapshot(qReq, (snapshot) => {
      setRequisitions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashRequisition)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'cash_requisitions');
      setLoading(false);
    });

    return () => {
      unsubscribeAccounts();
      unsubscribeInvoices();
      unsubscribeExpenses();
      unsubscribeReq();
    };
  }, []);

  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const selectedAccount = accounts.find(a => a.id === expenseFormData.accountId);
      
      const expenseData = {
        ...expenseFormData,
        amount: Number(expenseFormData.amount),
        date: Timestamp.fromDate(new Date(expenseFormData.date)),
        accountName: selectedAccount?.name || '',
        createdAt: Timestamp.now()
      };

      await addDoc(collection(db, 'expenses'), expenseData);

      // Update account balance
      if (selectedAccount) {
        await updateDoc(doc(db, 'accounts', selectedAccount.id), {
          balance: selectedAccount.balance - Number(expenseFormData.amount),
          updatedAt: Timestamp.now()
        });
      }

      toast.success('Expense added successfully');
      setIsExpenseModalOpen(false);
      setExpenseFormData({
        description: '',
        amount: 0,
        category: 'General',
        date: new Date().toISOString().split('T')[0],
        accountId: ''
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'expenses');
      toast.error('Failed to add expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteExpense = (expense: Expense) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Expense',
      message: `Are you sure you want to delete the expense "${expense.description}"?`,
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'expenses', expense.id));
          
          // Revert account balance
          if (expense.accountId) {
            const account = accounts.find(a => a.id === expense.accountId);
            if (account) {
              await updateDoc(doc(db, 'accounts', account.id), {
                balance: account.balance + expense.amount,
                updatedAt: Timestamp.now()
              });
            }
          }
          
          toast.success('Expense deleted');
        } catch (error) {
          toast.error('Failed to delete expense');
        }
      }
    });
  };

  const reportsRef = React.useRef<AccountingReportsRef>(null);
  const [showReportsMenu, setShowReportsMenu] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [reportDates, setReportDates] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [reportSections, setReportSections] = useState<string[]>(['revenue', 'profitability', 'assets', 'liabilities']);
  const [previewData, setPreviewData] = useState<PDFPreviewData | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false);
  const [reconcileData, setReconcileData] = useState<any[]>([]);
  const [reconciliationResults, setReconciliationResults] = useState<{
    matches: any[];
    mismatches: any[];
  } | null>(null);

  const reportOptions = [
    { id: 'trial', name: 'Trial Balance' },
    { id: 'pl', name: 'Profit & Loss' },
    { id: 'balance', name: 'Balance Sheet' },
    { id: 'ledger', name: 'General Ledger' },
    { id: 'financial', name: 'Financial Report' },
  ];

  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    try {
      let doc: jsPDF | undefined;
      let filename = '';

      switch (selectedReport) {
        case 'trial':
          doc = await reportsRef.current?.generateTrialBalance(reportDates.from, reportDates.to);
          filename = `Trial_Balance_${reportDates.to}.pdf`;
          break;
        case 'pl':
          doc = await reportsRef.current?.generateProfitLoss(reportDates.from, reportDates.to);
          filename = `Profit_Loss_${reportDates.from}_to_${reportDates.to}.pdf`;
          break;
        case 'balance':
          doc = await reportsRef.current?.generateBalanceSheet(reportDates.to);
          filename = `Balance_Sheet_${reportDates.to}.pdf`;
          break;
        case 'ledger':
          doc = await reportsRef.current?.generateLedger(reportDates.from, reportDates.to);
          filename = `General_Ledger_${reportDates.from}_to_${reportDates.to}.pdf`;
          break;
        case 'financial':
          doc = await reportsRef.current?.generateFinancial(reportDates.from, reportDates.to, reportSections);
          filename = `Financial_Report_${reportDates.from}_to_${reportDates.to}.pdf`;
          break;
      }

      if (doc) {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        setPreviewData({ url, doc, filename });
        setIsPreviewModalOpen(true);
        setIsReportModalOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report');
    }
  };

  const toggleSection = (section: string) => {
    setReportSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const downloadJournalTemplate = () => {
    const template = [
      { 'Account Name': 'Main Bank Account', 'System Balance': '', 'Bank Statement Balance': '', 'Difference': '', 'Remarks': '' },
      { 'Account Name': 'Petty Cash', 'System Balance': '', 'Bank Statement Balance': '', 'Difference': '', 'Remarks': '' },
    ];
    const csv = Papa.unparse(template);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, 'Bank_Reconciliation_Template.csv');
    toast.success('Template downloaded');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as any[];
        setReconcileData(data);
        
        // Analyze reconciliation
        const matches: any[] = [];
        const mismatches: any[] = [];

        data.forEach(row => {
          const systemAcc = accounts.find(a => a.name.toLowerCase() === row['Account Name']?.toLowerCase());
          const bankBalance = parseFloat(row['Bank Statement Balance'] || '0');
          const systemBalance = systemAcc ? systemAcc.balance : 0;
          
          const diff = Math.abs(systemBalance - bankBalance);
          const item = {
            name: row['Account Name'],
            systemBalance,
            bankBalance,
            diff,
            remarks: row['Remarks']
          };

          if (diff < 1) {
            matches.push(item);
          } else {
            mismatches.push(item);
          }
        });

        setReconciliationResults({ matches, mismatches });
      },
      error: (error) => {
        toast.error('Failed to parse file: ' + error.message);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);
  const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.totalAmount, 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const netProfit = totalPaid - totalExpenses;

  // Prepare chart data for last 6 months
  const chartData = Array.from({ length: 6 }).map((_, i) => {
    const monthDate = subMonths(new Date(), 5 - i);
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    
    const monthlyRevenue = invoices
      .filter(inv => inv.status === 'paid' && inv.createdAt && isWithinInterval(typeof inv.createdAt.toDate === 'function' ? inv.createdAt.toDate() : new Date(inv.createdAt as any), { start: monthStart, end: monthEnd }))
      .reduce((sum, inv) => sum + inv.totalAmount, 0);

    const monthlyExpenses = expenses
      .filter(exp => exp.date && isWithinInterval(typeof exp.date.toDate === 'function' ? exp.date.toDate() : new Date(exp.date as any), { start: monthStart, end: monthEnd }))
      .reduce((sum, exp) => sum + exp.amount, 0);

    return {
      name: format(monthDate, 'MMM'),
      revenue: monthlyRevenue,
      expenses: monthlyExpenses,
      profit: monthlyRevenue - monthlyExpenses
    };
  });

  // Expense breakdown by category
  const expenseBreakdown = expenseCategories.map(cat => {
    const amount = expenses
      .filter(exp => exp.category === cat)
      .reduce((sum, exp) => sum + exp.amount, 0);
    return { name: cat, value: amount };
  }).filter(item => item.value > 0);

  const COLORS = ['#1e40af', '#ea580c', '#16a34a', '#dc2626', '#9333ea', '#0891b2', '#4f46e5', '#be185d', '#15803d', '#44403c'];

  const recentInvoices = invoices.slice(0, 5);
  const recentExpenses = expenses.slice(0, 5);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'text-emerald-600 bg-emerald-50';
      case 'sent': return 'text-blue-600 bg-blue-50';
      case 'overdue': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-3 h-3" />;
      case 'overdue': return <AlertCircle className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Accounts Dashboard</h1>
          <p className="text-slate-500 font-medium">Financial overview and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <GenerateReportTrigger />
          <div className="relative">
            <button 
              onClick={() => setShowReportsMenu(!showReportsMenu)}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all shadow-lg shadow-slate-200"
            >
              <FileText className="w-5 h-5 text-blue-400" />
              Financial Reports
              <ChevronDown className={cn("w-4 h-4 transition-transform", showReportsMenu && "rotate-180")} />
            </button>

            <AnimatePresence>
              {showReportsMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowReportsMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-3 w-64 bg-white rounded-3xl shadow-2xl border border-slate-100 p-3 z-50 overflow-hidden"
                  >
                    <div className="p-3 mb-2">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Financial Reports</p>
                    </div>
                    <div className="space-y-1">
                      {reportOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => {
                            setSelectedReport(option.id);
                            setIsReportModalOpen(true);
                            setShowReportsMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-slate-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all"
                        >
                          <BarChart3 className="w-4 h-4" />
                          {option.name}
                        </button>
                      ))}
                     </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => setIsReconcileModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all shadow-lg shadow-slate-200"
          >
            <RefreshCw className="w-5 h-5 text-blue-400" />
            Reconciliation
          </button>

          <button 
            onClick={() => setIsExpenseModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200"
          >
            <Plus className="w-5 h-5" />
            Add Expense
          </button>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-bold text-slate-600 uppercase tracking-wider">Live Updates</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 p-1 bg-slate-100 rounded-2xl w-fit mb-8 flex-wrap">
        <button onClick={() => setActiveTab('overview')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer", activeTab === 'overview' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Overview</button>
        <button onClick={() => setActiveTab('invoices')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer", activeTab === 'invoices' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Invoices Module</button>
        <button onClick={() => setActiveTab('requisitions')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer", activeTab === 'requisitions' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Requisitions</button>
        <button onClick={() => setActiveTab('cashier')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer", activeTab === 'cashier' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Cashier</button>
      </div>

      {activeTab === 'overview' ? (
        <>
          <AccountingReports ref={reportsRef} accounts={accounts} invoices={invoices} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
              <Wallet className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Total Balance</h3>
          <p className="text-3xl font-black text-slate-900">
            <span className="text-blue-800 text-lg mr-1">TZS</span>
            {totalBalance.toLocaleString()}
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Total Revenue (Paid)</h3>
          <p className="text-3xl font-black text-slate-900">
            <span className="text-emerald-600 text-lg mr-1">TZS</span>
            {totalPaid.toLocaleString()}
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-600">
              <TrendingDown className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Total Expenses</h3>
          <p className="text-3xl font-black text-slate-900">
            <span className="text-red-600 text-lg mr-1">TZS</span>
            {totalExpenses.toLocaleString()}
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${netProfit >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Net Profit</h3>
          <p className={`text-3xl font-black ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            <span className="text-lg mr-1">TZS</span>
            {netProfit.toLocaleString()}
          </p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profit & Loss Chart */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-800">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Profit & Loss Analysis</h3>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Last 6 Months Comparison</p>
              </div>
            </div>
          </div>
          
          <div className="h-[350px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                  dy={10}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                />
                <Tooltip 
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ 
                    borderRadius: '16px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    padding: '12px'
                  }}
                  formatter={(value: any) => `TZS ${Number(value || 0).toLocaleString()}`}
                />
                <Bar dataKey="revenue" fill="#16a34a" radius={[4, 4, 0, 0]} name="Revenue" />
                <Bar dataKey="expenses" fill="#dc2626" radius={[4, 4, 0, 0]} name="Expenses" />
                <Bar dataKey="profit" fill="#1e40af" radius={[4, 4, 0, 0]} name="Net Profit" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Expense Breakdown */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"
        >
          <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-red-600" />
            Expense Breakdown
          </h3>
          <div className="h-[250px] w-full mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {expenseBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => `TZS ${Number(value || 0).toLocaleString()}`}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
            {expenseBreakdown.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span className="text-slate-600 font-medium">{item.name}</span>
                </div>
                <span className="font-bold text-slate-900">{item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Invoices */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Recent Invoices
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-slate-50">
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Invoice #</th>
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Client</th>
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Amount</th>
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentInvoices.map((invoice, index) => (
                  <tr key={`acc-inv-${invoice.id}-${index}`} className="group hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 font-bold text-slate-900">{invoice.invoiceNumber}</td>
                    <td className="py-4 text-slate-600 font-medium">{invoice.clientName}</td>
                    <td className="py-4 font-black text-slate-900">
                      {invoice.totalAmount.toLocaleString()}
                    </td>
                    <td className="py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        {invoice.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Recent Expenses */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Recent Expenses
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-slate-50">
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Description</th>
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Category</th>
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Amount</th>
                  <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentExpenses.map((expense, index) => (
                  <tr key={`acc-exp-${expense.id}-${index}`} className="group hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 font-bold text-slate-900">{expense.description}</td>
                    <td className="py-4">
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase rounded-md">
                        {expense.category}
                      </span>
                    </td>
                    <td className="py-4 font-black text-red-600">
                      {expense.amount.toLocaleString()}
                    </td>
                    <td className="py-4 text-right">
                      <button 
                        onClick={() => handleDeleteExpense(expense)}
                        className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </>
  ) : activeTab === 'invoices' ? (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-2 sm:p-6">
      <InvoicesPage />
    </div>
  ) : activeTab === 'requisitions' ? (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left bg-slate-50/50">
              <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Code</th>
              <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Site</th>
              <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Requested By</th>
              <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Amount</th>
              <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {requisitions.map((req, index) => (
              <tr key={`acc-req-${req.id}-${index}`} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-4 font-bold text-slate-900">{req.codeNo}</td>
                <td className="px-6 py-4 text-sm text-slate-600 font-medium">{req.siteName}</td>
                <td className="px-6 py-4 text-sm text-slate-600 font-medium">{req.requestedBy}</td>
                <td className="px-6 py-4 text-sm font-black text-slate-900">TZS {req.totalAmount.toLocaleString()}</td>
                <td className="px-6 py-4">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                    req.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : 
                    req.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'
                  )}>{req.status.replace(/_/g, ' ')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden p-8 text-center">
      <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
      <h3 className="text-xl font-bold text-slate-900 mb-2">Cashier Overview</h3>
      <p className="text-slate-500 mb-6">View and manage cash releases and account balances.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
        {accounts.filter(a => a.type === 'cash').map((acc, index) => (
          <div key={`acc-cash-${acc.id}-${index}`} className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{acc.name}</p>
            <p className="text-2xl font-black text-slate-900">TZS {acc.balance.toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  )}
      <AnimatePresence>
        {isExpenseModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsExpenseModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-slate-900">Add Expense</h2>
                <button onClick={() => setIsExpenseModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleExpenseSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Description *</label>
                  <input
                    required
                    type="text"
                    value={expenseFormData.description}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                    placeholder="e.g., Office Rent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Amount *</label>
                    <input
                      required
                      type="number"
                      value={expenseFormData.amount || ''}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: Number(e.target.value) })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Date *</label>
                    <input
                      required
                      type="date"
                      value={expenseFormData.date}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, date: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Category *</label>
                  <select
                    required
                    value={expenseFormData.category}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  >
                    {expenseCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Account *</label>
                  <select
                    required
                    value={expenseFormData.accountId}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, accountId: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  >
                    <option value="">Select Account</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name} ({acc.balance.toLocaleString()})</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Expense'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isReconcileModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReconcileModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Bank Reconciliation</h2>
                  <p className="text-sm font-medium text-slate-500">Match your system balances with bank statements</p>
                </div>
                <button onClick={() => setIsReconcileModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100">
                  <FileSpreadsheet className="w-8 h-8 text-blue-600 mb-4" />
                  <h3 className="font-bold text-slate-900 mb-2">Step 1: Download Template</h3>
                  <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                    Download the reconciliation journal template to prepare your bank data for upload.
                  </p>
                  <button
                    onClick={downloadJournalTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </button>
                </div>

                <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                  <Upload className="w-8 h-8 text-slate-600 mb-4" />
                  <h3 className="font-bold text-slate-900 mb-2">Step 2: Upload Journal</h3>
                  <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                    Upload your completed reconciliation journal (CSV) to compare records.
                  </p>
                  <label className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-all cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Choose File
                    <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} />
                  </label>
                </div>
              </div>

              {reconciliationResults && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-900 uppercase tracking-widest text-[10px]">Reconciliation Results</h3>
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1.5 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />
                        {reconciliationResults.matches.length} Matched
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                        <AlertCircle className="w-3 h-3" />
                        {reconciliationResults.mismatches.length} Mismatches
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {[...reconciliationResults.mismatches, ...reconciliationResults.matches].map((item, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "p-4 rounded-2xl border flex items-center justify-between",
                          item.diff < 1 ? "bg-green-50/50 border-green-100" : "bg-red-50/50 border-red-100"
                        )}
                      >
                        <div>
                          <p className="text-sm font-bold text-slate-900">{item.name}</p>
                          <div className="flex gap-4 mt-1">
                            <p className="text-[10px] font-medium text-slate-500">System: {Number(item.systemBalance).toLocaleString()} TZS</p>
                            <p className="text-[10px] font-medium text-slate-500">Bank: {Number(item.bankBalance).toLocaleString()} TZS</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={cn(
                            "text-sm font-black",
                            item.diff < 1 ? "text-green-600" : "text-red-600"
                          )}>
                            {item.diff < 1 ? 'MATCHED' : `${item.diff.toLocaleString()} DIFF`}
                          </p>
                          <p className="text-[10px] font-medium text-slate-400">{item.remarks || 'No remarks'}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      setIsReconcileModalOpen(false);
                      setReconciliationResults(null);
                      toast.success('Reconciliation analysis closed');
                    }}
                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all mt-4"
                  >
                    Finish Reconciliation
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isReportModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReportModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Generate Report</h2>
                  <p className="text-sm font-medium text-slate-500">{reportOptions.find(o => o.id === selectedReport)?.name}</p>
                </div>
                <button onClick={() => setIsReportModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                {selectedReport !== 'balance' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">From Date</label>
                      <input
                        type="date"
                        value={reportDates.from}
                        onChange={(e) => setReportDates({ ...reportDates, from: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
                      />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    {selectedReport === 'balance' ? 'As of Date' : 'To Date'}
                  </label>
                  <input
                    type="date"
                    value={reportDates.to}
                    onChange={(e) => setReportDates({ ...reportDates, to: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
                  />
                </div>

                {selectedReport === 'financial' && (
                  <div className="space-y-3 pt-4 border-t border-slate-100">
                    <label className="text-sm font-bold text-slate-900 block mb-2 uppercase tracking-widest text-[10px]">Included Sections</label>
                    {[
                      { id: 'revenue', label: 'Revenue & Expenditure' },
                      { id: 'profitability', label: 'Profitability' },
                      { id: 'assets', label: 'Assets' },
                      { id: 'liabilities', label: 'Liabilities' }
                    ].map((section) => (
                      <label key={section.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={reportSections.includes(section.id)}
                          onChange={() => toggleSection(section.id)}
                          className="w-4 h-4 rounded text-blue-800 focus:ring-blue-800/20 border-slate-200"
                        />
                        <span className="text-sm font-semibold text-slate-700">{section.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                <button
                  onClick={handleGenerateReport}
                  className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all shadow-lg shadow-slate-200 flex items-center justify-center gap-2 mt-6"
                >
                  <Download className="w-5 h-5 text-blue-400" />
                  Generate {reportOptions.find(o => o.id === selectedReport)?.name}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isPreviewModalOpen && previewData && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                URL.revokeObjectURL(previewData.url);
                setIsPreviewModalOpen(false);
                setPreviewData(null);
              }}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-5xl h-full bg-white rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 bg-white z-10">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Report Preview</h2>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">{previewData.filename}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      const printWindow = window.open(previewData.url);
                      if (printWindow) {
                        printWindow.print();
                      } else {
                        // Fallback for popup blockers
                        const iframe = document.createElement('iframe');
                        iframe.style.display = 'none';
                        iframe.src = previewData.url;
                        document.body.appendChild(iframe);
                        iframe.contentWindow?.print();
                      }
                    }}
                    className="flex items-center gap-2 px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-all"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                  <button
                    onClick={() => {
                      downloadFile(previewData.doc.output('blob'), previewData.filename);
                      toast.success('Download started');
                    }}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-800 text-white rounded-xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={() => {
                      URL.revokeObjectURL(previewData.url);
                      setIsPreviewModalOpen(false);
                      setPreviewData(null);
                    }}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors ml-4"
                  >
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* PDF Container */}
              <div className="flex-1 bg-slate-50 relative overflow-hidden">
                <iframe
                  src={previewData.url}
                  className="w-full h-full border-none shadow-inner"
                  title="PDF Preview"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
      />
    </div>
  );
};

export default AccountsDashboard;
